/* eslint-disable @typescript-eslint/no-unused-vars */
import { Duration, isAfter, max, sub } from 'date-fns';
import { FC } from 'react';
import React from 'react';
import { LineChart } from '@/components/tremor/line-chart';
import { DonutChart } from '@/components/tremor/pie-chart';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HeadingGroup } from '@/components/ui/heading-group';
import { strings } from '@/constants/strings';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import { useStatistics } from '@/hooks/use-statistics';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';

export const DashboardPage: FC = () => {
  const { organisation } = useCurrentOrganisation();

  const { data: statistics, isFetching: isFetchingStatistics } = useStatistics(
    organisation?.id ?? 0,
  );

  type stat = {
    date: Date;
    dateString?: string;
    online: number;
    offline: number;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const donutData: { name: string; amount: number }[] = [];
  let preformedData: stat[] = [];

  for (const item of statistics) {
    if (item.statistic) {
      const dateTime = new Date(item.createdAt);
      preformedData.push({
        date: dateTime,
        online: item.statistic.data.online,
        offline: item.statistic.data.offline,
      });
    }
  }

  preformedData.sort((a, b) => a.date.getTime() - b.date.getTime());

  preformedData = preformedData.map((item) => ({
    date: item.date,
    dateString: `${item.date.toDateString()} ${item.date.getHours()}:${item.date.getMinutes()} `,
    online: item.online ?? 0,
    offline: item.offline ?? 0,
  }));

  const [tableData, setTableData] = React.useState<stat[]>([...preformedData]);

  donutData.push({
    name: 'online',
    amount:
      preformedData.length > 1 && preformedData[preformedData.length - 1].online
        ? preformedData[preformedData.length - 1].online
        : 0,
  });
  donutData.push({
    name: 'offline',
    amount:
      preformedData.length > 1 &&
      preformedData[preformedData.length - 1].offline
        ? preformedData[preformedData.length - 1].offline
        : 0,
  });

  const simpleFilter = (x: stat[], period: Duration): stat[] => {
    const dates: Date[] = [];
    for (const i of preformedData) {
      dates.push(i.date);
    }
    let m: Date = max(dates);
    m = sub(m, period);
    const y = x.filter((item) => isAfter(item.date, m));
    return y;
  };

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitleTooltip={strings.dashboard.subtitle}
          title={strings.dashboard.title}
          tooltip={strings.dashboard.tooltip}
        />
      </HeadingGroupContainerLayout>
      <div className="grid grid-cols-3 gap-4">
        <Card className="col-span-2 flex flex-col gap-4">
          <h3>Online Devices</h3>
          {!isFetchingStatistics ? (
            <LineChart
              allowDecimals={false}
              categories={['online', 'offline']}
              className="h-80"
              colors={['lime', 'amber']}
              data={tableData}
              enableLegendSlider={true}
              index="dateString"
            />
          ) : (
            <p>loading...</p>
          )}
          <h3>Date Range</h3>
          <div className="flex grow gap-4">
            <Button
              onClick={() => {
                setTableData([...simpleFilter(preformedData, { years: 1 })]);
              }}
            >
              Last Year
            </Button>
            <Button
              onClick={() => {
                setTableData([...simpleFilter(preformedData, { months: 1 })]);
              }}
            >
              Last Month
            </Button>
            <Button
              onClick={() => {
                setTableData([...simpleFilter(preformedData, { weeks: 1 })]);
              }}
            >
              Last Week
            </Button>
            <Button
              onClick={() => {
                setTableData([...simpleFilter(preformedData, { days: 1 })]);
              }}
            >
              Last Day
            </Button>
            <Button
              onClick={() => {
                setTableData([...preformedData]);
              }}
            >
              Reset
            </Button>
          </div>
        </Card>
        <Card className="col-span-1 flex flex-col">
          <h3>Device Summary:</h3>
          <h4>
            {tableData.length > 1 && tableData[tableData.length - 1].dateString
              ? tableData[tableData.length - 1].dateString
              : ''}
          </h4>
          <div className="grow place-content-center">
            <DonutChart
              category="name"
              className="justify-self-center"
              colors={['lime', 'amber']}
              data={donutData}
              showLabel={true}
              value="amount"
            />
          </div>
        </Card>
      </div>
    </>
  );
};
